// packages import
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, CircularProgress, DialogActions } from "@mui/material"
import { FC, useContext } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
// component import
import { Alert } from "../common/Alert"
import { CustomController } from "../common/CustomController"
import { CustomModal } from "../common/CustomModal"
// others
import { CreateCourtInput, useCreateCourtMutation } from "../../../generated"
import { CANCEL } from "../../constants"
import { AppContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { AddNewCourtsProps, ErrorException } from "../../interfaceTypes"
import { createCourtValidationSchema } from "../../validationSchema"

export const AddNewCourt: FC<AddNewCourtsProps> = ({ refetch }) => {
  const { isOpen, dispatch } = useContext(AppContext)

  const methods = useForm<CreateCourtInput>({
    mode: "all",
    resolver: yupResolver(createCourtValidationSchema),

    defaultValues: {
      county: '',
      name: ''
    }
  })

  const { handleSubmit, reset } = methods;

  const [createCourtMutation, { loading }] = useCreateCourtMutation({
    onError({ message, graphQLErrors }) {
      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        const { createCourt: { response, court } } = data

        if (response) {
          const { status } = response

          if (status === 200 && court) {
            refetch && refetch()
            Alert.success('Court created successfully')
            dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
            dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
            handleClose()
            reset()
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<CreateCourtInput> = async (data: CreateCourtInput) => {
    const { name, county } = data

    await createCourtMutation({
      variables: {
        createCourtInput: {
          county,
          name
        }
      }
    })
  }

  const handleClose = () => isOpen && dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })

  return (
    <CustomModal title='Add New Court' isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
          <CustomController
            controllerName='name'
            controllerLabel='Court Name'
            fieldType='text'
          />

          <CustomController
            controllerName='county'
            controllerLabel='County'
            fieldType='text'
          />

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant='outlined' aria-label={CANCEL}>{CANCEL}</Button>

            <Button type="submit" color='primary' variant="contained" disabled={loading} aria-label='add court'
              endIcon={loading && <CircularProgress size={20} color="inherit" />}>
              Add Court
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}